import * as React from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  CircularProgress,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Download,
  Visibility,
  Description,
  PictureAsPdf,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import { useAuthUser } from '../../Services/Auth/sessionAuth';
import Utilisateur from '../../Models/Utilisateur';
import axios from 'axios';
import Data from '../../Data/Data.json';
import { Crypter } from '../../Functions/Security/Crypter';
import { apiEndpoints } from '../../config';
import Agence from '../../Models/Agence';

const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  html: 'text/html',
  csv: 'text/csv',
  txt: 'text/plain',
};

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'csv', label: 'CSV' },
  { value: 'html', label: 'HTML' },
];

interface ExtraFieldDef {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'select' | 'multiselect';
  options?: { value: string; label: string }[];
  defaultValue?: any;
  fetchUrl?: string;
  fetchParams?: Record<string, any>;
  valueField?: string;
  labelField?: string;
  joinParam?: string;
  dependsOn?: string;
  dependsOnParam?: string;
}

interface ReportLayoutProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  endpoint: string | ((params: Record<string, any>) => string);
  extraParams?: Record<string, any>;
  showClientRange?: boolean;
  showDateRange?: boolean;
  showAgence?: boolean;
  loadAgenceData?: boolean;
  extraFields?: ExtraFieldDef[];
  paramTransform?: (params: Record<string, any>, context: {
    dynamicOptions: Record<string, { value: string; label: string; raw?: any }[]>;
    agenceData: Agence[];
  }) => Record<string, any>;
  fileNamePrefix?: string;
}

export default function ReportLayout({
  title,
  subtitle,
  icon,
  endpoint,
  extraParams = {},
  showClientRange = true,
  showDateRange = true,
  showAgence = true,
  loadAgenceData = false,
  extraFields = [],
  paramTransform,
  fileNamePrefix = 'rapport',
}: ReportLayoutProps) {
  const theme = useTheme();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  // State
  const [dataAgence, setDataAgence] = React.useState<Agence[]>([]);
  const [selectedAgences, setSelectedAgences] = React.useState<string[]>([]);
  const [dateDebut, setDateDebut] = React.useState(user.datjour.toString().slice(0, 10));
  const [dateFin, setDateFin] = React.useState(user.datjour.toString().slice(0, 10));
  const [numCliD, setNumCliD] = React.useState('');
  const [numCliF, setNumCliF] = React.useState('');
  const [format, setFormat] = React.useState('pdf');
  const [extraValues, setExtraValues] = React.useState<Record<string, any>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [blobData, setBlobData] = React.useState<Blob | null>(null);
  const [zoom, setZoom] = React.useState(100);
  const [dynamicOptions, setDynamicOptions] = React.useState<Record<string, { value: string; label: string; raw?: any }[]>>({});
  const [multiSelected, setMultiSelected] = React.useState<Record<string, string[]>>({});

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 200));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 50));
  const handleResetZoom = () => setZoom(100);

  // Load agences
  React.useEffect(() => {
    if (showAgence || loadAgenceData) {
      const config = {
        method: 'get' as const,
        maxBodyLength: Infinity,
        url: Data.ApiUrl.allBranchs,
        headers: { Authorization: Data.SecretKey.ApiToken },
      };
      axios(config).then((response) => {
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            setDataAgence(res.Result);
          }
        }
      });
    }
    // Initialize extra fields defaults
    const initExtras: Record<string, any> = {};
    const initMulti: Record<string, string[]> = {};
    extraFields.forEach((f) => {
      if (f.defaultValue !== undefined) initExtras[f.key] = f.defaultValue;
      if (f.type === 'multiselect') initMulti[f.key] = [];
    });
    setExtraValues(initExtras);
    setMultiSelected(initMulti);

    // Fetch dynamic options for fields with fetchUrl and without dependsOn
    extraFields.forEach(async (f) => {
      if ((f.type === 'multiselect' || (f.type === 'select' && f.fetchUrl)) && f.fetchUrl && !f.dependsOn) {
        try {
          const resp = await axios.get(`${apiEndpoints.fastReport}/${f.fetchUrl}`, {
            params: f.fetchParams || {},
          });
          const items = resp.data || [];
          const opts = items.map((item: any) => ({
            value: item[f.valueField || 'Code'],
            label: item[f.labelField || 'Libelle'],
            raw: item,
          }));
          setDynamicOptions((prev) => ({ ...prev, [f.key]: opts }));
        } catch (err) {
          console.error(`Error fetching options for ${f.key}:`, err);
        }
      }
    });
  }, []);

  // Watch dependency field changes for cascading selects
  const depSignature = extraFields
    .filter((f) => f.dependsOn)
    .map((f) => `${f.key}:${extraValues[(f as any).dependsOn] || ''}`)
    .join('|');

  React.useEffect(() => {
    if (!depSignature) return;
    extraFields.forEach(async (f) => {
      if (f.dependsOn && f.fetchUrl) {
        const depValue = extraValues[f.dependsOn];
        if (!depValue) {
          setDynamicOptions((prev) => ({ ...prev, [f.key]: [] }));
          return;
        }
        try {
          const resp = await axios.get(`${apiEndpoints.fastReport}/${f.fetchUrl}`, {
            params: {
              ...(f.fetchParams || {}),
              [f.dependsOnParam || f.dependsOn]: depValue,
            },
          });
          const items = resp.data || [];
          const opts = items.map((item: any) => ({
            value: item[f.valueField || 'Code'],
            label: item[f.labelField || 'Libelle'],
            raw: item,
          }));
          setDynamicOptions((prev) => ({ ...prev, [f.key]: opts }));
        } catch (err) {
          console.error(`Error fetching cascading options for ${f.key}:`, err);
        }
      }
    });
  }, [depSignature]);

  const buildParams = () => {
    const CodAgce2 = user?.codage || '';
    const agenceInfo = dataAgence.find((b) => String(b.CodeAgence) === CodAgce2);
    const ListAgce = selectedAgences.join(',') || CodAgce2;
    const NomUser = user?.nomuti || '';

    const params: Record<string, any> = {
      CodAgce2,
      Libelle: agenceInfo?.Libelle || '',
      Banque: agenceInfo?.Banque || '',
      Teleph: agenceInfo?.Telephone || '',
      NumReg: agenceInfo?.NumReg || '',
      Licence: agenceInfo?.Licence || '',
      NumCon: agenceInfo?.Licence || '',
      BPoste: agenceInfo?.BPoste || '',
      Ville: agenceInfo?.Ville || '',
      NomUser,
      ListAgce,
      ...extraParams,
    };

    if (showClientRange) {
      params.NumCliD = numCliD;
      params.NumCliF = numCliF;
    }
    if (showDateRange) {
      params.dateDebut = dateDebut;
      params.dateFin = dateFin;
    }
    // Add extra field values
    Object.entries(extraValues).forEach(([k, v]) => {
      params[k] = v;
    });
    // Add multiselect joined values
    Object.entries(multiSelected).forEach(([k, v]) => {
      const fieldDef = extraFields.find((f) => f.key === k);
      const joinParam = fieldDef?.joinParam || k;
      params[joinParam] = v.join(',') || '00000';
    });

    return params;
  };

  const handleGenerate = async (previewMode: boolean = false) => {
    setLoading(true);
    setError(null);

    const requestFormat = previewMode ? 'pdf' : format;
    let params : Record<string, any> = { ...buildParams(), format: requestFormat };

    if (paramTransform) {
      params = paramTransform(params, { dynamicOptions, agenceData: dataAgence });
    }

    const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(params) : endpoint;

    try {
      const response = await axios.get(`${apiEndpoints.fastReport}/${resolvedEndpoint}`, {
        params,
        responseType: 'blob',
      });

      const mimeType = MIME_TYPES[requestFormat] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: mimeType });
      setBlobData(blob);

      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
      const url = window.URL.createObjectURL(blob);
      setBlobUrl(url);

      if (!previewMode) {
        // Direct download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileNamePrefix}.${requestFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error('Erreur lors de la génération du rapport:', err);
      setError('Erreur lors de la génération du rapport. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFromPreview = () => {
    if (!blobData) return;
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileNamePrefix}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Title bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
          }}
        >
          {icon || <Description sx={{ fontSize: 24 }} />}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Main content: filters (left) + preview (right) */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left: Filters panel */}
        <Paper elevation={1} sx={{
          p: 2,
          borderRadius: 2,
          width: { xs: '100%', md: 360 },
          flexShrink: 0,
          overflowY: 'auto',
          maxHeight: { xs: 'none', md: 'calc(100vh - 140px)' },
        }}>
        <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          Critères de sélection
        </Typography>

        <Grid container spacing={2}>
          {/* Agence multi-select */}
          {showAgence && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Agence</InputLabel>
                <Select
                  multiple
                  value={selectedAgences}
                  label="Agence"
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    if (value.includes('ALL')) {
                      if (selectedAgences.length === dataAgence.length) {
                        setSelectedAgences([]);
                      } else {
                        setSelectedAgences(dataAgence.map((a) => a.CodeAgence));
                      }
                    } else {
                      setSelectedAgences(value);
                    }
                  }}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map((code) => {
                        const agence = dataAgence.find((a) => a.CodeAgence === code);
                        return agence ? `${agence.CodeAgence} - ${agence.Libelle}` : code;
                      })
                      .join(', ')
                  }
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value="ALL">
                    <Checkbox
                      checked={selectedAgences.length === dataAgence.length && dataAgence.length > 0}
                    />
                    <ListItemText
                      primary={selectedAgences.length === dataAgence.length ? 'Tout décocher' : 'Tout cocher'}
                    />
                  </MenuItem>
                  {dataAgence.map((option) => (
                    <MenuItem key={option.CodeAgence} value={option.CodeAgence}>
                      <Checkbox checked={selectedAgences.includes(option.CodeAgence)} />
                      <ListItemText primary={`${option.CodeAgence} - ${option.Libelle}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Client range */}
          {showClientRange && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="N° client début"
                  size="small"
                  fullWidth
                  value={numCliD}
                  onChange={(e) => setNumCliD(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="N° client fin"
                  size="small"
                  fullWidth
                  value={numCliF}
                  onChange={(e) => setNumCliF(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            </>
          )}

          {/* Date range */}
          {showDateRange && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Date début"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Date fin"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            </>
          )}

          {/* Extra fields */}
          {extraFields.map((field) => {
            if (field.type === 'multiselect') {
              const opts = dynamicOptions[field.key] || [];
              const selected = multiSelected[field.key] || [];
              return (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      multiple
                      label={field.label}
                      value={selected}
                      onChange={(e) => {
                        const value = e.target.value as string[];
                        if (value.includes('ALL')) {
                          if (selected.length === opts.length) {
                            setMultiSelected((prev) => ({ ...prev, [field.key]: [] }));
                          } else {
                            setMultiSelected((prev) => ({ ...prev, [field.key]: opts.map((o) => o.value) }));
                          }
                        } else {
                          setMultiSelected((prev) => ({ ...prev, [field.key]: value }));
                        }
                      }}
                      renderValue={(sel) =>
                        (sel as string[])
                          .map((code) => {
                            const opt = opts.find((o) => o.value === code);
                            return opt ? `${opt.value} - ${opt.label}` : code;
                          })
                          .join(', ')
                      }
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      sx={{ borderRadius: '10px' }}
                    >
                      <MenuItem value="ALL">
                        <Checkbox checked={selected.length === opts.length && opts.length > 0} />
                        <ListItemText primary={selected.length === opts.length ? 'Tout décocher' : 'Tout cocher'} />
                      </MenuItem>
                      {opts.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox checked={selected.includes(opt.value)} />
                          <ListItemText primary={`${opt.value} - ${opt.label}`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            }
            const isDisabled = field.dependsOn ? !extraValues[field.dependsOn] : false;
            const selectOpts = field.fetchUrl ? (dynamicOptions[field.key] || []) : (field.options || []);
            return (
              <Grid item xs={12} sm={6} key={field.key}>
                {field.type === 'select' ? (
                  <FormControl fullWidth size="small" disabled={isDisabled}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={extraValues[field.key] || ''}
                      onChange={(e) => {
                        setExtraValues((prev) => {
                          const newVals = { ...prev, [field.key]: e.target.value };
                          extraFields.forEach((ef) => {
                            if (ef.dependsOn === field.key) newVals[ef.key] = '';
                          });
                          return newVals;
                        });
                      }}
                      sx={{ borderRadius: '10px' }}
                    >
                      {selectOpts.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label={field.label}
                    size="small"
                    fullWidth
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={extraValues[field.key] || ''}
                    onChange={(e) => {
                      setExtraValues((prev) => {
                        const newVals = { ...prev, [field.key]: e.target.value };
                        extraFields.forEach((ef) => {
                          if (ef.dependsOn === field.key) newVals[ef.key] = '';
                        });
                        return newVals;
                      });
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Action bar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Format selector */}
          <FormControl size="small" fullWidth>
            <InputLabel>Format</InputLabel>
            <Select
              label="Format"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              sx={{ borderRadius: '10px' }}
            >
              {FORMAT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preview button */}
          <Tooltip title="Aperçu du rapport">
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              startIcon={loading ? <CircularProgress size={18} /> : <Visibility />}
              onClick={() => handleGenerate(true)}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                borderColor: '#cbd5e1',
                color: '#334155',
                '&:hover': { borderColor: theme.palette.primary.main, bgcolor: '#f8fafc' },
              }}
            >
              Aperçu
            </Button>
          </Tooltip>

          {/* Download button */}
          <Button
            variant="contained"
            size="medium"
            fullWidth
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Download />}
            onClick={() => handleGenerate(false)}
            disabled={loading}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
            }}
          >
            Télécharger
          </Button>

          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Right: PDF Preview panel */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: { xs: 400, md: 0 },
        }}
      >
        {/* Preview header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdf sx={{ color: '#ef4444', fontSize: 22 }} />
            <Typography variant="subtitle2" fontWeight="bold" color="#334155">
              {blobUrl ? `${fileNamePrefix}.pdf` : 'Aperçu'}
            </Typography>
          </Box>

          {blobUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Zoom controls */}
              <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 50}>
                <ZoomOut fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ cursor: 'pointer', minWidth: 36, textAlign: 'center' }}
                onClick={handleResetZoom}
              >
                {zoom}%
              </Typography>
              <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 200}>
                <ZoomIn fontSize="small" />
              </IconButton>

              {/* Download from preview */}
              <IconButton size="small" onClick={handleDownloadFromPreview} sx={{ ml: 0.5 }}>
                <Download fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Preview content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto',
            bgcolor: '#e2e8f0',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="textSecondary">
                Génération du rapport en cours...
              </Typography>
            </Box>
          ) : blobUrl ? (
            <Box
              component="iframe"
              src={blobUrl}
              sx={{
                width: `${zoom}%`,
                height: '100%',
                border: 'none',
                transition: 'width 0.2s ease',
                bgcolor: '#fff',
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
              <PictureAsPdf sx={{ fontSize: 56 }} />
              <Typography variant="body2">
                Cliquez sur « Aperçu » pour générer le rapport
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}
